<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import { flip } from 'svelte/animate';
  import { AtSign, Plus, Search, Users, X } from 'lucide-svelte';
  import { boardApi } from '$lib/api/board';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import BoardCardSourceBadge from '$lib/components/ui/BoardCardSourceBadge.svelte';
  import BoardSeenBadge from '$lib/components/ui/BoardSeenBadge.svelte';
  import { trackBoardView } from '$lib/utils/boardViewTracker';
  import { session } from '$lib/state/session.svelte';
  import BoardDueBadge from '$lib/components/ui/BoardDueBadge.svelte';
  import BoardDateInput from '$lib/components/ui/BoardDateInput.svelte';
  import BoardUpdatesSheet from './BoardUpdatesSheet.svelte';
  import { selectsRequester } from '$lib/utils/boardHandoff';
  import Modal from '$lib/components/ui/Modal.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import { t } from '$lib/i18n';
  import type { MessageKey } from '$lib/i18n/en';
   import type { BoardColumn, TeamBoardCard, TeamBoardPerson, TeamBoardUsageRow } from '$lib/types';

  const COLUMNS: { id: BoardColumn; labelKey: MessageKey; dot: string }[] = [
    { id: 'todo', labelKey: 'board.column.todo', dot: '#94A2B0' },
    { id: 'doing', labelKey: 'board.column.doing', dot: '#226A73' },
    { id: 'blocked', labelKey: 'board.column.blocked', dot: '#C8412B' },
    { id: 'done', labelKey: 'board.column.done', dot: '#5B7A00' }
  ];

  function colLabel(id: BoardColumn): string {
    return t(COLUMNS.find((c) => c.id === id)!.labelKey);
  }

  let selectedPeople = $state<Set<number>>(new Set());
  let selectedCol = $state<BoardColumn>('todo');
  let mobilePeopleOpen = $state(false);
  let desktopPeopleOpen = $state(false);
  let activityOpen = $state(false);
   let attentionOpen = $state(false);
   let usageOpen = $state(false);
  let cardSearch = $state('');
  let peopleSearch = $state('');

  const queryClient = useQueryClient();

  function refreshTeam() {
    queryClient.invalidateQueries({ queryKey: ['board-team'] });
  }

  function onPeopleKeydown(e: KeyboardEvent) {
    if (mobilePeopleOpen && e.key === 'Escape') mobilePeopleOpen = false;
  }

  const teamQuery = createQuery(() => ({
    queryKey: ['board-team'],
    queryFn: () => boardApi.team()
  }));
   const activityQuery = createQuery(() => ({ queryKey: ['board-team-activity', [...selectedPeople].join(',')], queryFn: () => boardApi.teamActivity([...selectedPeople]), enabled: activityOpen }));
   const usageQuery = createQuery(() => ({ queryKey: ['board-team-usage'], queryFn: () => boardApi.teamUsage(), enabled: usageOpen }));
   function usageAge(row: TeamBoardUsageRow) { return row.board_opened_at ? Math.floor((Date.now() - new Date(row.board_opened_at).getTime()) / 86400000) : Infinity; }

  const allCards = $derived(teamQuery.data?.cards ?? []);
  const peopleFilteredCards = $derived(
    selectedPeople.size === 0 ? allCards : allCards.filter((c) => selectedPeople.has(c.user_id))
  );
  const normalizedCardSearch = $derived(cardSearch.trim().toLocaleLowerCase());
  const filteredCards = $derived(
    !normalizedCardSearch
      ? peopleFilteredCards
      : peopleFilteredCards.filter((card) =>
          [card.title, card.description, card.user_name].some((value) =>
            value.toLocaleLowerCase().includes(normalizedCardSearch)
          )
        )
  );
  const peopleForRail = $derived(
    !peopleSearch.trim()
      ? teamQuery.data?.people ?? []
      : (teamQuery.data?.people ?? []).filter((person) =>
          `${person.name} ${person.email}`.toLocaleLowerCase().includes(peopleSearch.trim().toLocaleLowerCase())
        )
  );

  let columnItems = $state<Record<BoardColumn, TeamBoardCard[]>>({ todo: [], doing: [], blocked: [], done: [] });
  let lastSyncedFrom: TeamBoardCard[] | undefined;
  $effect(() => {
    const data = filteredCards;
    if (data === lastSyncedFrom) return;
    lastSyncedFrom = data;
    columnItems = {
      todo: data.filter((c) => c.col === 'todo'),
      doing: data.filter((c) => c.col === 'doing'),
      blocked: data.filter((c) => c.col === 'blocked'),
      done: data.filter((c) => c.col === 'done')
    };
  });

  function cardsForCol(col: BoardColumn): TeamBoardCard[] {
    return columnItems[col];
  }

  function cardCountForPerson(id: number): number {
    return allCards.filter((c) => c.user_id === id).length;
  }

  const isAll = $derived(selectedPeople.size === 0);
  const selectedPerson = $derived.by(() => selectedPeople.size === 1 ? teamQuery.data?.people.find((p) => p.id === [...selectedPeople][0]) : undefined);

  const filterSummary = $derived.by(() => {
    if (isAll) return t('teamBoard.filterAll');
    if (selectedPeople.size === 1) {
      const id = [...selectedPeople][0];
      return teamQuery.data?.people.find((p) => p.id === id)?.name ?? t('teamBoard.peopleSelected', { n: 1 });
    }
    return t('teamBoard.peopleSelected', { n: selectedPeople.size });
  });
  const boardSummary = $derived(t('teamBoard.summary', { shown: filteredCards.length, total: allCards.length }));
  const boardEmptyMessage = $derived(
    normalizedCardSearch && peopleFilteredCards.length > 0
      ? t('teamBoard.emptySearch')
      : t('teamBoard.emptyBoard')
  );

  // Needs-attention view: blocked chains plus cards sitting in Doing untouched
  // for 3+ days. Derived from the already-loaded team cards — no extra fetch.
  // Age is whole days since the card was last touched; oldest first.
  const STUCK_DOING_DAYS = 3;
  function cardAgeDays(card: { updated_at: string }): number {
    return Math.max(0, Math.floor((Date.now() - new Date(card.updated_at).getTime()) / 86400000));
  }
  const attentionBlocked = $derived(
    allCards.filter((c) => c.col === 'blocked').sort((a, b) => cardAgeDays(b) - cardAgeDays(a))
  );
  const attentionStale = $derived(
    allCards
      .filter((c) => c.col === 'doing' && cardAgeDays(c) >= STUCK_DOING_DAYS)
      .sort((a, b) => cardAgeDays(b) - cardAgeDays(a))
  );
  const attentionCount = $derived(attentionBlocked.length + attentionStale.length);
  const cardById = $derived.by(() => {
    const map = new Map<number, TeamBoardCard>();
    for (const c of allCards) map.set(c.id, c);
    return map;
  });
  type ChainRow = { card: TeamBoardCard; depth: number };
  // Flatten each blocked chain depth-first (parent, helpers, sub-helpers),
  // guarding against revisits so a loop can never hang the render.
  function chainRows(root: TeamBoardCard): ChainRow[] {
    const rows: ChainRow[] = [{ card: root, depth: 0 }];
    const seen = new Set<number>([root.id]);
    const walk = (card: TeamBoardCard, depth: number) => {
      for (const helper of card.blockers ?? []) {
        const child = cardById.get(helper.card_id);
        if (!child || seen.has(child.id)) continue;
        seen.add(child.id);
        rows.push({ card: child, depth: depth + 1 });
        walk(child, depth + 1);
      }
    };
    walk(root, 0);
    return rows;
  }

  function selectAll() {
    selectedPeople = new Set();
  }

  function clearFilters() {
    selectAll();
    cardSearch = '';
    peopleSearch = '';
  }

  function togglePerson(id: number) {
    const next = new Set(selectedPeople);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedPeople = next;
  }

  function personActive(id: number): boolean {
    return !isAll && selectedPeople.has(id);
  }

  let editing = $state<TeamBoardCard | null>(null);
  let creatingFor = $state<{ person: TeamBoardPerson; col: BoardColumn } | null>(null);
  let showForm = $state(false);
  let form = $state({ title: '', description: '', due_date: '' });
  let updateCard = $state<TeamBoardCard | null>(null);
  let updateText = $state('');
  function openUpdates(card: TeamBoardCard, text = '') { updateText = text; updateCard = card; }
  let saving = $state(false);
  let error = $state('');

  function openEdit(card: TeamBoardCard) {
    editing = card;
    form = { title: card.title, description: card.description, due_date: card.due_date?.slice(0, 10) ?? '' };
    error = '';
    showForm = true;
  }

  function openCreate(person: TeamBoardPerson, col: BoardColumn) {
    editing = null;
    creatingFor = { person, col };
    form = { title: '', description: '', due_date: '' };
    error = '';
    showForm = true;
  }

  async function save() {
    if (!form.title.trim()) {
      error = t('board.error.titleRequired');
      return;
    }
    saving = true;
    error = '';
    try {
      if (editing) await boardApi.teamUpdate(editing.id, { ...form, due_date: form.due_date });
      else if (creatingFor) await boardApi.teamCreate({ user_id: creatingFor.person.id, col: creatingFor.col, title: form.title.trim(), description: form.description, due_date: form.due_date });
      else return;
      refreshTeam();
      showForm = false;
      toast.success(editing ? t('board.toast.updated') : t('board.toast.created'));
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function remove(card: TeamBoardCard) {
    if (!confirm(t('board.confirmDelete', { title: card.title }))) return;
    try {
      await boardApi.teamRemove(card.id);
      refreshTeam();
      toast.success(t('board.toast.deleted'));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function moveCard(card: TeamBoardCard, col: BoardColumn) {
    if (col === card.col) return;
    try {
      await boardApi.teamUpdate(card.id, { col });
      refreshTeam();
      toast.success(t('board.toast.moved', { col: colLabel(col) }));
    } catch (err) {
      toast.error(getErrorMessage(err));
      refreshTeam();
    }
  }

  function handleConsider(col: BoardColumn, e: CustomEvent<DndEvent<TeamBoardCard>>) {
    columnItems[col] = e.detail.items;
  }

  function handleFinalize(col: BoardColumn, e: CustomEvent<DndEvent<TeamBoardCard>>) {
    columnItems[col] = e.detail.items;
    const moved = e.detail.items.find((c) => c.col !== col);
    if (moved) moveCard(moved, col);
  }

  function cardMenu(card: TeamBoardCard) {
    const items: { label: string; onClick: () => void; danger?: boolean }[] = [
      { label: t('board.updates.add'), onClick: () => openUpdates(card) },
      { label: t('common.edit'), onClick: () => openEdit(card) }
    ];
    if (card.col === 'blocked') items.push({ label: t('board.block.setInfo'), onClick: () => openTeamBlock(card) });
    for (const c of COLUMNS) {
      if (c.id !== card.col) items.push({ label: t('board.moveTo', { col: t(c.labelKey) }), onClick: () => moveCard(card, c.id) });
    }
    items.push({ label: t('board.deleteCard'), onClick: () => remove(card), danger: true });
    return items;
  }

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  let composer = $state('');
  let composerOpen = $state(false);
  let recipientQuery = $state('');
  let composerPeople = $state<TeamBoardPerson[]>([]);
  let composerActive = $state(0);
  let composerError = $state('');
  let composerSaving = $state(false);
  let composerDismissed = $state(false);
  const composerQuery = $derived(recipientQuery.replace(/^@/, '').trim().toLocaleLowerCase());

  function matchRank(person: TeamBoardPerson, query: string): number | null {
    if (!query) return 0;
    const local = person.email.split('@')[0];
    const values = [person.name, local, person.email].map((v) => v.toLocaleLowerCase());
    for (const value of values) if (value.startsWith(query)) return 1;
    for (const value of values) if (value.split(/[\s._-]+/).some((word) => word.startsWith(query))) return 2;
    for (const value of values) {
      let i = 0;
      for (const char of value) if (char === query[i]) i++;
      if (i === query.length) return 3;
    }
    return null;
  }

  const composerSuggestions = $derived.by(() => (teamQuery.data?.people ?? [])
    .map((person) => ({ person, rank: matchRank(person, composerQuery) }))
    .filter((item) => !composerPeople.some((person) => person.id === item.person.id))
    .filter((item): item is { person: TeamBoardPerson; rank: number } => item.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.person.name.localeCompare(b.person.name))
    .slice(0, 6)
    .map((item) => item.person));
  const composerSuggestionsOpen = $derived(Boolean(recipientQuery.startsWith('@') && !composerDismissed && composerSuggestions.length));

  function chooseComposerPerson(person: TeamBoardPerson) {
    if (!composerPeople.some((p) => p.id === person.id)) composerPeople = [...composerPeople, person];
    recipientQuery = '';
    composerActive = 0;
    composerError = '';
  }

  function composerKeydown(e: KeyboardEvent) {
    if (!composerSuggestionsOpen) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); composerActive = (composerActive + 1) % composerSuggestions.length; }
    else if (e.key === 'ArrowUp') { e.preventDefault(); composerActive = (composerActive - 1 + composerSuggestions.length) % composerSuggestions.length; }
    else if (e.key === 'Enter') { e.preventDefault(); chooseComposerPerson(composerSuggestions[composerActive]); }
    else if (e.key === 'Escape') { composerActive = 0; composerDismissed = true; }
  }

  async function submitComposer() {
    const task = composer.trim();
    if (!composerPeople.length || !task) {
      composerError = t('teamBoard.composerInvalid');
      return;
    }
    composerSaving = true;
    composerError = '';
    try {
      await boardApi.teamAssign({ user_ids: composerPeople.map((p) => p.id), col: 'todo', title: task });
      composer = '';
      composerPeople = [];
      recipientQuery = '';
      composerOpen = false;
      refreshTeam();
      toast.success(t('board.toast.created'));
    } catch (err) {
      composerError = getErrorMessage(err);
    } finally {
      composerSaving = false;
    }
  }

  // Manager blocking-info flow: attach helpers or an external party to any
  // card, including backfilling cards blocked before blocking info existed.
  let teamBlockingCard = $state<TeamBoardCard | null>(null);
  let teamBlockReason = $state('');
  let teamBlockMode = $state<'helpers' | 'external'>('helpers');
  let teamExternalName = $state('');
  type TeamHelperRow = { user_id: number | null; title: string };
  let teamHelpers = $state<TeamHelperRow[]>([]);
  let teamBlockSaving = $state(false);
  let teamBlockError = $state('');
  function teamNeedsBlockingInfo(card: TeamBoardCard): boolean {
    return card.col === 'blocked' && !(card.blockers?.length) && !card.blocked_external_name;
  }
  function openTeamBlock(card: TeamBoardCard) {
    teamBlockingCard = card;
    teamBlockReason = card.blocked_reason ?? '';
    teamExternalName = card.blocked_external_name ?? '';
    teamBlockMode = card.blocked_external_name ? 'external' : 'helpers';
    teamHelpers = [{ user_id: null, title: '' }];
    teamBlockError = '';
  }
  const teamBlockRequesterSelected = $derived(Boolean(teamBlockingCard && teamBlockMode === 'helpers' && selectsRequester(teamBlockingCard, teamHelpers)));
  const teamBlockValid = $derived(!teamBlockRequesterSelected && (
    teamBlockMode === 'external'
      ? Boolean(teamBlockReason.trim() && teamExternalName.trim())
      : Boolean(teamBlockReason.trim() && teamHelpers.length > 0 && teamHelpers.every((x) => x.user_id && x.title.trim()))
  ));
  function teamHelperOptions(except: number | null): TeamBoardPerson[] {
    return (teamQuery.data?.people ?? []).filter((p) => p.id !== teamBlockingCard?.user_id && (except == null || p.id === except || !teamHelpers.some((x) => x.user_id === p.id)));
  }
  async function submitTeamBlock() {
    if (!teamBlockingCard || !teamBlockValid || teamBlockSaving) return;
    teamBlockSaving = true;
    teamBlockError = '';
    try {
      const body =
        teamBlockMode === 'external'
          ? { reason: teamBlockReason.trim(), helpers: [], external: { name: teamExternalName.trim() } }
          : { reason: teamBlockReason.trim(), helpers: teamHelpers.map((x) => ({ user_id: x.user_id!, title: x.title.trim() })) };
      await boardApi.teamBlock(teamBlockingCard.id, body);
      teamBlockingCard = null;
      refreshTeam();
      toast.success(t('board.toast.blocked'));
    } catch (err) {
      teamBlockError = getErrorMessage(err);
    } finally {
      teamBlockSaving = false;
    }
  }
</script>

<svelte:window onkeydown={onPeopleKeydown} />

<div class="tym-page tym-team-board">
  <Toolbar
    searchValue={cardSearch}
    searchPlaceholder={t('teamBoard.searchCards')}
    searchLabel={t('teamBoard.searchCards')}
    onSearch={(value) => (cardSearch = value)}
  >
    {#snippet filters()}
      <button type="button" class="btn-secondary hidden !min-h-[36px] !px-3 !text-[12.5px] md:inline-flex" aria-expanded={desktopPeopleOpen} onclick={() => (desktopPeopleOpen = !desktopPeopleOpen)}>
        <Users class="h-4 w-4" strokeWidth={2} />
        {filterSummary}
      </button>
      <button type="button" class="btn-secondary !min-h-[44px] !px-3 !text-[12.5px] md:hidden" aria-expanded={mobilePeopleOpen} onclick={() => (mobilePeopleOpen = true)}>
        <Users class="h-4 w-4" strokeWidth={2} />
        {filterSummary}
      </button>
    {/snippet}
    {#snippet actions()}
      <button type="button" class="btn-secondary !min-h-[44px]" onclick={() => (attentionOpen = true)}>
        {t('teamBoard.attention')}{#if attentionCount > 0}<span class="tym-chip-count">{attentionCount}</span>{/if}
      </button>
       <button type="button" class="btn-secondary !min-h-[44px]" onclick={() => (activityOpen = true)}>{t('teamBoard.activity')}</button>
       <button type="button" class="btn-secondary !min-h-[44px]" onclick={() => (usageOpen = true)}>{t('teamBoard.usage')}</button>
    {/snippet}
  </Toolbar>

  {#if teamQuery.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if teamQuery.isError}
    <div class="tym-error-state flex flex-wrap items-center justify-between gap-3">
      <span>{t('teamBoard.error')}</span>
      <button type="button" class="btn-secondary !min-h-[36px] !px-3 !text-[12.5px]" onclick={refreshTeam}>
        {t('common.retry')}
      </button>
    </div>
  {:else if teamQuery.data}
    <div class="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[10px] border border-toyama-cloudy-gray bg-surface px-3 py-2 text-[12px] text-dark-gray">
      <span class="font-semibold text-navy">{boardSummary}</span>
      <span aria-hidden="true">·</span>
      <span>{filterSummary}</span>
      {#if cardSearch || peopleSearch || !isAll}
        <button type="button" class="ml-auto text-[12px] font-semibold text-toyama-teal underline underline-offset-2" onclick={clearFilters}>{t('teamBoard.clearFilters')}</button>
      {/if}
    </div>

    <div class="tym-team-board-workspace">
      {#if desktopPeopleOpen}
      <aside
        class="tym-people-rail hidden min-h-0 shrink-0 md:flex md:w-[220px] md:flex-col"
        aria-label={t('teamBoard.filterPeople')}
      >
        <div class="flex items-center justify-between gap-2">
          <p class="tym-people-rail-heading !mb-0">{t('teamBoard.people')}</p>
          <button type="button" class="tym-touch flex items-center justify-center rounded-lg text-dark-gray hover:bg-surface-2" aria-label={t('common.close')} onclick={() => (desktopPeopleOpen = false)}>
            <X class="h-4 w-4" />
          </button>
        </div>
        <label class="relative mb-2 block">
          <span class="sr-only">{t('teamBoard.searchPeople')}</span>
          <Search class="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-dark-gray" />
          <input class="input min-h-[38px] py-1.5 pl-8 text-[12.5px]" type="search" placeholder={t('teamBoard.searchPeople')} bind:value={peopleSearch} />
        </label>
        <button
          type="button"
          class="tym-people-rail-item {isAll ? 'is-active' : ''}"
          aria-pressed={isAll}
          onclick={selectAll}
        >
          <span class="min-w-0 flex-1 truncate">{t('teamBoard.filterAll')}</span>
          <span class="tym-chip-count">{allCards.length}</span>
        </button>
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain" role="group" aria-label={t('teamBoard.people')}>
          {#each peopleForRail as person (person.id)}
            <button
              type="button"
              class="tym-people-rail-item {personActive(person.id) ? 'is-active' : ''}"
              aria-pressed={personActive(person.id)}
              onclick={() => togglePerson(person.id)}
            >
              <span class="min-w-0 flex-1 truncate">{person.name}</span>
              <span class="tym-chip-count">{cardCountForPerson(person.id)}</span>
            </button>
          {:else}
            <p class="tym-board-column-empty">{t('teamBoard.noPeopleMatch')}</p>
          {/each}
        </div>
      </aside>
      {/if}

      <div class="flex min-h-0 min-w-0 flex-1 flex-col">
        <FilterChipRow
          class="tym-board-chiprow mb-2 shrink-0 md:hidden"
          chips={COLUMNS.map((c) => ({ id: c.id, label: t(c.labelKey), count: cardsForCol(c.id).length }))}
          value={selectedCol}
          onSelect={(id) => (selectedCol = id as BoardColumn)}
          ariaLabel={t('board.columnAria')}
          sticky
        />

        {#if filteredCards.length === 0}
          <div class="mb-3 rounded-[14px] border border-dashed border-toyama-cloudy-gray bg-surface-2 px-5 py-5 text-center">
            <p class="font-heading text-sm font-semibold text-navy">{boardEmptyMessage}</p>
            {#if cardSearch || !isAll}
              <button type="button" class="mt-3 min-h-[44px] text-[13px] font-semibold text-toyama-teal underline underline-offset-2" onclick={clearFilters}>{t('teamBoard.clearFilters')}</button>
            {/if}
          </div>
        {/if}
        <div class="tym-team-board-columns">
          {#each COLUMNS as c (c.id)}
            <div class="min-h-0 {selectedCol === c.id ? 'flex' : 'hidden'} md:flex">
              <section class="tym-board-column flex min-h-0 w-full flex-col overflow-hidden">
                <div class="tym-board-column-header z-10 shrink-0 bg-surface-2">
                  <span class="h-[7px] w-[7px] shrink-0 rounded-full" style="background:{c.dot}"></span>
                  <p class="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-navy">{t(c.labelKey)}</p>
                  <span class="tym-board-column-count" aria-hidden="true">{cardsForCol(c.id).length}</span>
                  {#if selectedPerson}
                    <button type="button" class="ml-auto tym-touch flex items-center justify-center rounded-md text-dark-gray hover:bg-surface-2" aria-label={t('board.addCardTo', { col: t(c.labelKey) })} onclick={() => openCreate(selectedPerson, c.id)}>
                      <Plus class="h-4 w-4" strokeWidth={2.5} />
                    </button>
                  {/if}
                </div>
                <div class="tym-board-column-scroll relative min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
                  <div
                    class="flex min-h-full flex-col gap-2"
                    use:dndzone={{ items: columnItems[c.id], flipDurationMs: 150, delayTouchStart: true }}
                    onconsider={(e) => handleConsider(c.id, e)}
                    onfinalize={(e) => handleFinalize(c.id, e)}
                  >
                    {#each columnItems[c.id] as card (card.id)}
                      <article
                        use:trackBoardView={{ card, userId: session.me?.id }}
                        animate:flip={{ duration: 150 }}
                        class="tym-board-card min-w-0 cursor-grab overflow-hidden rounded-[10px] border border-toyama-cloudy-gray bg-surface py-3 pl-3 pr-2"
                        style="--tym-board-accent:{c.dot}"
                      >
                        <div class="flex items-start gap-1.5">
                          <div class="min-w-0 flex-1">
                            <p class="text-[10.5px] font-semibold uppercase tracking-[.06em] text-dark-gray">{card.user_name}</p>
                            <p class="mt-1 break-words text-[13px] font-semibold leading-[1.35] text-navy" style="text-wrap:pretty">{card.title}</p>
                            {#if card.description}
                              <p class="mt-1 break-words text-[11.5px] leading-[1.4] text-dark-gray" style="overflow-wrap:anywhere;text-wrap:pretty">{card.description}</p>
                            {/if}
                            <BoardCardSourceBadge {card} />
                            <BoardDueBadge {card} />
                            <BoardSeenBadge {card} />
                            <button type="button" class="mt-1 min-h-[44px] rounded-md px-2 text-xs font-semibold text-toyama-teal hover:bg-surface-2" onclick={() => openUpdates(card)}>{t('board.updates.add')}</button>
                            {#if card.group_total > 1}<p class="mt-1 text-[11px] text-dark-gray">{t('board.sharedProgress', { completed: card.group_completed, total: card.group_total })}</p>{/if}
                            {#if card.col === 'blocked' && card.blocked_reason}<p class="mt-1 text-[11px] text-red-700">{t('board.blockedReason', { reason: card.blocked_reason })}</p>{/if}
                            {#if card.col === 'blocked' && card.blocked_external_name}<p class="mt-1 text-[11px] text-dark-gray">{t('board.blockedExternal', { name: card.blocked_external_name })}</p>{/if}
                            {#if card.blockers?.length}<p class="mt-1 text-[11px] text-dark-gray">{t('board.helpers', { helpers: card.blockers.map((x) => `${x.user_name} (${x.col})`).join(', ') })}</p>{/if}
                            {#if card.blocking}<p class="mt-1 text-[11px] text-dark-gray">{t('board.blocking', { user: card.blocking.user_name, title: card.blocking.title })}</p>{/if}
                            {#if teamNeedsBlockingInfo(card)}<button type="button" class="mt-1.5 min-h-[44px] rounded-md bg-red-50 px-2 text-left text-[11.5px] font-semibold text-red-700" onclick={() => openTeamBlock(card)}>{t('board.block.missingInfoCta')}</button>{/if}
                            {#if card.col === 'done' && card.done_at}
                              <span
                                class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-surface-2 px-1.5 py-px text-[10.5px] tabular-nums text-dark-gray"
                              >
                                {t('board.doneAt', { date: fmtDate(card.done_at) })}
                              </span>
                            {/if}
                          </div>
                          <RowActions items={cardMenu(card)} />
                        </div>
                      </article>
                    {/each}
                  </div>
                  {#if columnItems[c.id].length === 0}
                    <p class="tym-board-column-empty">{t('teamBoard.emptyColumn')}</p>
                  {/if}
                </div>
              </section>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}

  {#if mobilePeopleOpen && teamQuery.data}
    <button
      type="button"
      class="fixed inset-x-0 bottom-0 top-16 z-50 bg-[#0A1626]/50 md:hidden"
      aria-label={t('common.close')}
      onclick={() => (mobilePeopleOpen = false)}
    ></button>
    <div
      class="tym-people-rail fixed bottom-0 left-0 top-16 z-50 flex w-[min(280px,85vw)] flex-col md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t('teamBoard.selectPeople')}
      style="padding-top:calc(12px + env(safe-area-inset-top))"
    >
      <div class="mb-2 flex items-center justify-between gap-2 px-2">
        <p class="tym-people-rail-heading !mb-0">{t('teamBoard.selectPeople')}</p>
        <button type="button" class="tym-touch flex items-center justify-center rounded-lg text-dark-gray hover:bg-surface-2" onclick={() => (mobilePeopleOpen = false)}>
          <X class="h-5 w-5" strokeWidth={2} />
        </button>
      </div>
      <div class="flex-1 overflow-y-auto px-1 pb-4">
        <label class="relative mb-2 block px-1">
          <span class="sr-only">{t('teamBoard.searchPeople')}</span>
          <Search class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-dark-gray" />
          <input class="input min-h-[44px] py-1.5 pl-8 text-[13px]" type="search" placeholder={t('teamBoard.searchPeople')} bind:value={peopleSearch} />
        </label>
        <button
          type="button"
          class="tym-people-rail-item {isAll ? 'is-active' : ''}"
          aria-pressed={isAll}
          onclick={() => {
            selectAll();
            mobilePeopleOpen = false;
          }}
        >
          <span class="min-w-0 flex-1 truncate">{t('teamBoard.filterAll')}</span>
          <span class="tym-chip-count">{allCards.length}</span>
        </button>
        {#each peopleForRail as person (person.id)}
          <button
            type="button"
            class="tym-people-rail-item {personActive(person.id) ? 'is-active' : ''}"
            aria-pressed={personActive(person.id)}
            onclick={() => togglePerson(person.id)}
          >
            <span class="min-w-0 flex-1 truncate">{person.name}</span>
            <span class="tym-chip-count">{cardCountForPerson(person.id)}</span>
          </button>
        {:else}
          <p class="tym-board-column-empty">{t('teamBoard.noPeopleMatch')}</p>
        {/each}
      </div>
      <div class="border-t border-toyama-cloudy-gray p-3">
        <button type="button" class="btn-primary w-full !min-h-[44px]" onclick={() => (mobilePeopleOpen = false)}>
          {t('common.close')}
        </button>
      </div>
    </div>
  {/if}

  {#if showForm && (editing || creatingFor)}
    <Modal title={editing ? t('board.editCard') : t('teamBoard.newCardFor', { person: creatingFor!.person.name, col: colLabel(creatingFor!.col) })} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">{t('board.title')}</div>
          <input type="text" class="input" aria-label={t('board.title')} bind:value={form.title} />
        </div>
        <div>
          <div class="label">{t('board.description')}</div>
          <textarea class="input" rows="2" aria-label={t('board.description')} bind:value={form.description}></textarea>
        </div>
        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <BoardDateInput bind:value={form.due_date} />
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>{t('common.cancel')}</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  {/if}

  {#if teamQuery.data}
    <button
      type="button"
      class="tym-team-board-fab"
      aria-label={t('teamBoard.composerLabel')}
      title={t('teamBoard.composerLabel')}
      onclick={() => {
        composerError = '';
        composerDismissed = false;
        composerOpen = true;
      }}
    >
      <AtSign class="h-6 w-6" strokeWidth={2.25} />
    </button>
  {/if}
</div>

{#if updateCard}
  {#key updateCard.id}<BoardUpdatesSheet card={updateCard} team initialText={updateText} onClose={() => (updateCard = null)} onSaved={refreshTeam} />{/key}
{/if}

{#if usageOpen}
  <Modal title={t('teamBoard.usageTitle')} onClose={() => (usageOpen = false)} wide>
    {#if usageQuery.isLoading}
      <div class="tym-loading-state">{t('common.loading')}</div>
    {:else if usageQuery.isError}
      <div class="tym-error-state">{t('teamBoard.error')}</div>
    {:else}
    <div class="overflow-x-auto"><table class="w-full text-left text-sm"><thead><tr class="border-b"><th class="p-2">{t('teamBoard.usageName')}</th><th class="p-2">{t('teamBoard.usageLastOpened')}</th><th class="p-2">{t('teamBoard.usageOpens')}</th></tr></thead><tbody>
      {#each usageQuery.data ?? [] as row}
        <tr class:!bg-red-50={usageAge(row) >= 3} class="border-b"><td class="p-2">{row.name}<div class="text-xs text-gray-500">{row.email}</div></td><td class="p-2">{row.board_opened_at ? t('teamBoard.usageAgo', { n: usageAge(row) }) : t('teamBoard.usageNever')}</td><td class="p-2">{row.board_open_count}</td></tr>
      {/each}
    </tbody></table></div>
    {/if}
  </Modal>
{/if}

{#if composerOpen}
  <Modal title={t('teamBoard.composerLabel')} onClose={() => (composerOpen = false)}>
    <form id="team-assign-form" class="space-y-4" onsubmit={(e) => { e.preventDefault(); submitComposer(); }}>
      <div>
        <label class="label" for="team-assign-recipients">{t('teamBoard.composerLabel')}</label>
        {#if composerPeople.length}
          <div class="mb-2 flex flex-wrap gap-1.5" aria-label={t('teamBoard.composerLabel')}>
            {#each composerPeople as person (person.id)}<span class="inline-flex min-h-[36px] items-center gap-1 rounded-full bg-surface-2 py-1 pl-3 pr-1 text-xs text-navy">{person.name}<button type="button" class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-toyama-cloudy-gray" aria-label={t('teamBoard.removeRecipient', { name: person.name })} onclick={() => composerPeople = composerPeople.filter((p) => p.id !== person.id)}>×</button></span>{/each}
          </div>
        {/if}
        <input id="team-assign-recipients" role="combobox" class="input min-h-[44px] w-full" autocomplete="off" placeholder={t('teamBoard.composerPlaceholder')} bind:value={recipientQuery} oninput={() => { composerActive = 0; composerDismissed = false; composerError = ''; }} onkeydown={composerKeydown} aria-autocomplete="list" aria-controls={composerSuggestionsOpen ? 'team-assign-suggestions' : undefined} aria-activedescendant={composerSuggestionsOpen ? `team-assign-suggestion-${composerSuggestions[composerActive].id}` : undefined} aria-expanded={composerSuggestionsOpen} />
        {#if composerSuggestionsOpen}
          <div id="team-assign-suggestions" role="listbox" class="mt-2 max-h-52 overflow-y-auto rounded-lg border border-toyama-cloudy-gray bg-surface p-1">
            {#each composerSuggestions as person, i (person.id)}
              <button id={`team-assign-suggestion-${person.id}`} type="button" role="option" aria-selected={i === composerActive} class="block min-h-[44px] w-full rounded px-3 py-2 text-left text-sm hover:bg-surface-2 {i === composerActive ? 'bg-surface-2' : ''}" onmousedown={(e) => e.preventDefault()} onclick={() => chooseComposerPerson(person)}>{person.name} <span class="text-dark-gray">{person.email}</span></button>
            {/each}
          </div>
        {/if}
      </div>
      <label class="label">{t('board.title')}<input class="input mt-1 min-h-[44px] w-full" placeholder={t('teamBoard.composerTaskPlaceholder')} bind:value={composer} oninput={() => (composerError = '')} /></label>
      {#if composerError}<p class="text-xs text-red-700" role="alert">{composerError}</p>{/if}
    </form>
    {#snippet footer()}
      <button type="submit" form="team-assign-form" class="btn-primary min-h-[44px] w-full" disabled={composerSaving}>{composerSaving ? t('common.saving') : t('teamBoard.assign')}</button>
    {/snippet}
  </Modal>
{/if}

  {#if teamBlockingCard}
    <Modal title={t('board.block.title')} onClose={() => (teamBlockingCard = null)}>
      <div class="space-y-4">
        <p class="text-sm text-dark-gray">{t('board.block.intro', { title: teamBlockingCard.title })} <span class="font-semibold">{teamBlockingCard.user_name}</span></p>
        {#if teamBlockingCard.blocking}<p class="text-sm font-semibold text-navy">{t('board.return.helping', { owner: teamBlockingCard.blocking.user_name, title: teamBlockingCard.blocking.title })}</p>{/if}
        <label class="label">{t('board.block.reason')}<input class="input mt-1 min-h-[44px]" bind:value={teamBlockReason} /></label>
        <div class="flex gap-2" role="group" aria-label={t('board.block.modeLabel')}>
          <button type="button" class="btn-secondary min-h-[44px] flex-1 {teamBlockMode === 'helpers' ? '!border-toyama-teal !text-navy' : ''}" aria-pressed={teamBlockMode === 'helpers'} onclick={() => (teamBlockMode = 'helpers')}>{t('board.block.modeHelpers')}</button>
          <button type="button" class="btn-secondary min-h-[44px] flex-1 {teamBlockMode === 'external' ? '!border-toyama-teal !text-navy' : ''}" aria-pressed={teamBlockMode === 'external'} onclick={() => (teamBlockMode = 'external')}>{t('board.block.modeExternal')}</button>
        </div>
        {#if teamBlockMode === 'external'}
          <label class="label">{t('board.block.externalName')}<input class="input mt-1 min-h-[44px]" autocomplete="off" bind:value={teamExternalName} placeholder={t('board.block.externalNamePlaceholder')} /></label>
          <p class="text-xs leading-5 text-dark-gray">{t('board.block.externalGuidance')}</p>
        {:else}
          {#each teamHelpers as helper, i}
            <fieldset class="rounded-[10px] border border-toyama-cloudy-gray p-3">
              <legend class="px-1 text-xs font-semibold text-navy">{t('board.block.helperNumber', { n: i + 1 })}</legend>
              <label class="label">{t('board.block.helperPerson')}
                <select class="input mt-1 min-h-[44px]" bind:value={helper.user_id} aria-label={t('board.block.helperPerson')}>
                  <option value={null}>{t('board.block.helperPersonPlaceholder')}</option>
                  {#each teamHelperOptions(helper.user_id) as person (person.id)}<option value={person.id}>{person.name} — {person.email}</option>{/each}
                </select>
              </label>
              <label class="label mt-3">{t('board.block.helperTask')}<input class="input mt-1 min-h-[44px]" bind:value={helper.title} /></label>
              {#if teamHelpers.length > 1}<button type="button" class="mt-3 min-h-[44px] px-2 text-sm font-semibold text-red-700" onclick={() => teamHelpers = teamHelpers.filter((_, n) => n !== i)}>{t('board.block.removeHelper')}</button>{/if}
            </fieldset>
          {/each}
        {/if}
        {#if teamBlockRequesterSelected}
          <p class="text-sm text-red-700" role="alert">{t('board.return.parentSelected')}</p>
          <button type="button" class="btn-secondary min-h-[44px]" onclick={() => { openUpdates(teamBlockingCard!, teamBlockReason); teamBlockingCard = null; }}>{t('board.return.action')}</button>
        {/if}
        {#if teamBlockError}<div class="tym-error-state" role="alert">{teamBlockError}</div>{/if}
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {#if teamBlockMode === 'helpers'}
          <button type="button" class="btn-secondary min-h-[44px]" onclick={() => teamHelpers = [...teamHelpers, { user_id: null, title: '' }]}>{t('board.block.addHelper')}</button>
          {:else}<span></span>{/if}
          <button type="button" class="btn-primary min-h-[44px]" disabled={teamBlockSaving || !teamBlockValid} onclick={submitTeamBlock}>{teamBlockSaving ? t('common.saving') : t('board.block.submit')}</button>
        </div>
      </div>
    </Modal>
  {/if}

{#if attentionOpen}
  <Modal title={t('teamBoard.attentionTitle')} onClose={() => (attentionOpen = false)} wide>
    {#if attentionCount === 0}
      <p class="text-[13px] text-dark-gray">{t('teamBoard.attentionEmpty')}</p>
    {:else}
      {#if attentionBlocked.length}
        <h3 class="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-navy">{t('teamBoard.attentionBlocked', { n: attentionBlocked.length })}</h3>
        <ul class="mb-5 mt-2 space-y-3">
          {#each attentionBlocked as root (root.id)}
            <li class="rounded-[10px] border border-toyama-cloudy-gray bg-surface-2 p-3">
              {#each chainRows(root) as row (row.card.id)}
                <div class="py-0.5 text-[13px] leading-[1.4]" style="margin-left:{row.depth * 18}px">
                  {#if row.depth > 0}<span aria-hidden="true" class="mr-1 text-dark-gray">↳</span>{/if}<span class="font-semibold text-navy">{row.card.user_name}</span><span class="text-dark-gray"> — {row.card.title}</span>
                  <span class="ml-1 rounded-full bg-surface px-1.5 py-px text-[10.5px] tabular-nums text-dark-gray">{t('teamBoard.ageDays', { n: cardAgeDays(row.card) })} · {row.card.col}</span>
                  {#if row.depth === 0}
                    {#if row.card.blocked_reason}<p class="mt-0.5 text-[12px] text-red-700">{t('board.blockedReason', { reason: row.card.blocked_reason })}</p>{/if}
                    {#if row.card.blocked_external_name}<p class="mt-0.5 text-[12px] text-dark-gray">{t('board.blockedExternal', { name: row.card.blocked_external_name })}</p>{/if}
                  {/if}
                </div>
              {/each}
            </li>
          {/each}
        </ul>
      {/if}
      {#if attentionStale.length}
        <h3 class="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-navy">{t('teamBoard.attentionStale', { n: attentionStale.length })}</h3>
        <ul class="mt-2 space-y-2">
          {#each attentionStale as card (card.id)}
            <li class="flex flex-wrap items-baseline gap-x-2 rounded-[10px] border border-toyama-cloudy-gray bg-surface-2 px-3 py-2 text-[13px] leading-[1.4]">
              <span class="font-semibold text-navy">{card.user_name}</span>
              <span class="min-w-0 flex-1 break-words text-dark-gray">{card.title}</span>
              <span class="rounded-full bg-surface px-1.5 py-px text-[10.5px] tabular-nums text-dark-gray">{t('teamBoard.attentionStaleAge', { age: t('teamBoard.ageDays', { n: cardAgeDays(card) }) })}</span>
            </li>
          {/each}
        </ul>
      {/if}
    {/if}
  </Modal>
{/if}

{#if activityOpen}
  <Modal title={t('teamBoard.activityTitle')} onClose={() => (activityOpen = false)}>
    {#if activityQuery.isLoading}<div class="tym-loading-state">{t('teamBoard.activityLoading')}</div>{:else if activityQuery.isError}<div class="tym-error-state">{getErrorMessage(activityQuery.error)}</div>{:else}
      <ul class="space-y-3 text-[13px]">{#each activityQuery.data?.events ?? [] as event (event.id)}<li><b>{event.card_title}</b> — {event.owner_name}: {event.event_type === 'status_changed' ? `${event.from_col} → ${event.to_col}` : event.event_type} by {event.actor_name || t('teamBoard.activitySystem')} · {new Date(event.created_at).toLocaleString()}</li>{:else}<li class="text-dark-gray">{t('teamBoard.activityEmpty')}</li>{/each}</ul>
    {/if}
  </Modal>
{/if}
